import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Check, X, ExternalLink, Edit, FileText } from 'lucide-react';
import { toast } from 'sonner';

export default function ApprovalsPage() {
  const [approvals, setApprovals] = useState([]);
  const [clubApprovals, setClubApprovals] = useState([]);
  // Comments are keyed by approval_id so multiple cards on screen keep
  // their own draft comment. Previously a single shared string reset
  // every time another card was clicked.
  const [commentsById, setCommentsById] = useState({});
  const [busyId, setBusyId] = useState(null);
  
  const fetchApprovals = async () => {
    try {
      const response = await axios.get(`${API}/approvals`, { withCredentials: true });
      setApprovals(response.data);
    } catch (error) {
      console.error('Error fetching approvals:', error);
    }
    try {
      const r2 = await axios.get(`${API}/club-approvals`, { withCredentials: true });
      setClubApprovals(r2.data || []);
    } catch (error) {
      // OK for non-supers
    }
  };
  
  useEffect(() => {
    fetchApprovals();
  }, []);

  const handleClubAction = async (requestId, action) => {
    try {
      await axios.post(`${API}/club-approvals/${requestId}/${action}`, {}, { withCredentials: true });
      toast.success(`Request ${action === 'approve' ? 'approved' : 'rejected'}.`);
      fetchApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process request');
    }
  };
  
  const handleAction = async (approvalId, action) => {
    const comment = (commentsById[approvalId] || '').trim();
    if (!comment && action !== 'approved') {
      toast.error('Please add a reason before rejecting');
      return;
    }
    setBusyId(approvalId);
    try {
      await axios.post(`${API}/approvals/${approvalId}/comment`, {
        comment: comment || 'Approved',
        action
      }, { withCredentials: true });
      toast.success(action === 'approved' ? 'Page approved' : 'Page rejected');
      setCommentsById(prev => {
        const { [approvalId]: _drop, ...rest } = prev;
        return rest;
      });
      fetchApprovals();
    } catch (error) {
      toast.error('Failed to process request');
    } finally {
      setBusyId(null);
    }
  };
  
  const getRequestTypeLabel = (approval) => {
    if (approval.request_type === 'edit') {
      return (
        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
          <Edit className="mr-1 h-3 w-3" />
          Edit Request
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
        <FileText className="mr-1 h-3 w-3" />
        New Page
      </Badge>
    );
  };
  
  return (
    <div className="space-y-6 fade-in">
      <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: '#2D4A3E' }}>
        Approval Requests
      </h1>
      
      <p className="text-muted-foreground">
        Review competition pages before they go live. Use the preview link to check content is appropriate. Submitters are emailed automatically when you approve or reject, so review notes on rejections should list the exact changes required.
      </p>

      {/* Club-related approvals (new-club signups + club edits) */}
      {clubApprovals.length > 0 && (
        <div className="space-y-3" data-testid="club-approvals-section">
          <h2 className="text-2xl font-semibold" style={{ color: '#2D4A3E' }}>Club Requests</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {clubApprovals.map(req => (
              <Card key={req.request_id} className="p-5 card-hover" data-testid={`club-approval-${req.request_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <Badge className="bg-amber-100 text-amber-800">
                    {req.request_type === 'signup_with_new_club' ? 'New Signup + Club' : 'Club Edit'}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {new Date(req.created_at).toLocaleString()}
                  </span>
                </div>
                {req.request_type === 'signup_with_new_club' ? (
                  <div className="mb-3">
                    <div className="font-medium">{req.user_name} <span className="text-xs text-muted-foreground">({req.user_email})</span></div>
                    <div className="text-sm mt-2">
                      <div><strong>Proposed club:</strong> {req.new_club?.name}</div>
                      <div className="text-muted-foreground">
                        {req.new_club?.short_name} / {req.new_club?.abbreviation} · {req.new_club?.competition_region}
                      </div>
                      <div className="text-xs mt-1">
                        Artistic: {req.new_club?.offers_artistic ? 'Yes' : 'No'} · Figures: {req.new_club?.offers_figures ? 'Yes' : 'No'}
                      </div>
                      <div className="text-xs">Contact: {req.new_club?.contact_email}</div>
                    </div>
                  </div>
                ) : (
                  <div className="mb-3 text-sm">
                    <div><strong>{req.requested_by_name}</strong> proposed changes to <strong>{req.club_name}</strong>:</div>
                    <pre className="text-xs bg-stone-50 rounded p-2 mt-1 overflow-x-auto">
                      {JSON.stringify(req.changes || {}, null, 2)}
                    </pre>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => handleClubAction(req.request_id, 'approve')}
                          data-testid={`club-approve-${req.request_id}`}>
                    <Check className="mr-2 h-4 w-4" /> Approve
                  </Button>
                  <Button variant="destructive" className="flex-1"
                          onClick={() => handleClubAction(req.request_id, 'reject')}
                          data-testid={`club-reject-${req.request_id}`}>
                    <X className="mr-2 h-4 w-4" /> Reject
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {approvals.map(approval => (
          <Card key={approval.approval_id} className="p-6 card-hover" data-testid={`approval-card-${approval.approval_id}`}>
            <div className="flex items-center justify-between mb-3">
              <Badge className={
                approval.status === 'approved' ? 'bg-green-100 text-green-800' :
                approval.status === 'rejected' ? 'bg-red-100 text-red-800' :
                'bg-yellow-100 text-yellow-800'
              }>
                {approval.status}
              </Badge>
              {getRequestTypeLabel(approval)}
            </div>
            
            {/* Page and Competition Info */}
            <div className="mb-4">
              <h3 className="font-semibold text-lg text-stone-800">
                {approval.page_title || 'Untitled Page'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {approval.competition_name || 'Unknown Competition'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Requested by: {approval.requester_name || approval.requested_by}
              </p>
            </div>
            
            {/* Preview Link */}
            <a 
              href={`/admin/preview/${approval.page_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 mb-4 font-medium"
              data-testid={`preview-link-${approval.approval_id}`}
            >
              <ExternalLink className="h-4 w-4" />
              Preview Page (opens in new tab)
            </a>
            
            {approval.comments.length > 0 && (
              <div className="mb-4 space-y-2">
                <p className="text-sm font-medium">Comments:</p>
                {approval.comments.map((c, i) => (
                  <div key={i} className="text-sm bg-stone-50 p-2 rounded">
                    <p className="font-medium">{c.user_name}</p>
                    <p className="text-muted-foreground">{c.comment}</p>
                  </div>
                ))}
              </div>
            )}
            
            {approval.status === 'pending' && (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Review notes <span className="italic">(required to reject — these notes will be emailed to the submitter as the required changes)</span>
                  </Label>
                  <Input
                    value={commentsById[approval.approval_id] || ''}
                    onChange={(e) => setCommentsById(prev => ({ ...prev, [approval.approval_id]: e.target.value }))}
                    placeholder="On reject, list the specific changes the submitter needs to make…"
                    data-testid={`comment-input-${approval.approval_id}`}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    onClick={() => handleAction(approval.approval_id, 'approved')}
                    disabled={busyId === approval.approval_id}
                    data-testid={`approve-button-${approval.approval_id}`}
                  >
                    <Check className="mr-2 h-4 w-4" />
                    {busyId === approval.approval_id ? 'Working…' : 'Approve'}
                  </Button>
                  <Button
                    variant="destructive"
                    className="flex-1"
                    onClick={() => handleAction(approval.approval_id, 'rejected')}
                    disabled={busyId === approval.approval_id}
                    data-testid={`reject-button-${approval.approval_id}`}
                  >
                    <X className="mr-2 h-4 w-4" />
                    Reject
                  </Button>
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>
      
      {approvals.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No approval requests at this time.</p>
        </div>
      )}
    </div>
  );
}
